import { type ReactNode, useId, useState } from "react";

/**
 * Connecting accounts (v42–v47): Flinks and Wealthica connect one institution per session, so the
 * panel lists what is connected and asks whether there is another. Four are included on each side and
 * each one past that is $1.50 a month, said before the button that adds it. Flinks is live where the
 * backend has it configured (flinks/FlinksConnections.tsx, which stops at four); otherwise the connect
 * button is disabled and onboarding shows the empty start state (v47).
 */
export const CONN_FREE = 4;
export const CONN_FEE = 1.5;

export type Connector = "Flinks" | "Wealthica";

/** "Learn more about Flinks / Wealthica": opens in place, not in a dialog (v46). */
export function LearnMore({ about, children }: { about: Connector; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  return (
    <>
      <button
        type="button"
        className="lmore"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen(!open)}
        data-testid={`lmore-${about.toLowerCase()}`}
      >
        Learn more about {about}
        <span className="cv" aria-hidden="true">
          &#9662;
        </span>
      </button>
      <div className="lbody" id={id} hidden={!open}>
        {children}
      </div>
    </>
  );
}

/** What the vendors say about themselves (v46), attributed to them: ported as written. */
export function FlinksPanel() {
  return (
    <LearnMore about="Flinks">
      <p>
        <b>Flinks</b> is a Montreal company that connects apps to Canadian banks. National Bank of
        Canada holds a majority stake in it, raised from 28% to 80% in a C$103 million deal.
      </p>
      <p>
        You sign in <b>on Flinks, never on our site</b>, so WealthyPlanet never sees your banking
        password. What comes back is read-only: balances and transactions. Nothing can move money in
        either direction.
      </p>
      <p>
        Flinks stores sensitive data with AES-256 encryption, sends it over HTTPS with 256-bit
        encryption, and holds a SOC 2 attestation. That attestation is theirs, not
        WealthyPlanet&rsquo;s.{" "}
        <a href="https://flinks.com/security/" target="_blank" rel="noopener noreferrer">
          Read their security page
        </a>
        .
      </p>
    </LearnMore>
  );
}

export function WealthicaPanel() {
  return (
    <LearnMore about="Wealthica">
      <p>
        <b>Wealthica</b> is a Canadian company that connects investment accounts to apps like this
        one. Its platform is <b>strictly read-only</b> &mdash; it cannot trade, withdraw or
        transfer, and neither can we.
      </p>
      <p>
        Your data is encrypted with AES-256 where it sits and TLS 1.2 in transit, and it is held on
        servers in Montreal. Wealthica states that some of the services processing it still sit in
        the US and that it is working to move them to Canada.
      </p>
      <p>
        Wealthica is SOC 2 Type 2 compliant, supports two-factor authentication, and lets you delete
        your account in one click. The attestation is theirs, not WealthyPlanet&rsquo;s.{" "}
        <a href="https://www.wealthica.com/security" target="_blank" rel="noopener noreferrer">
          Read their security page
        </a>
        .
      </p>
    </LearnMore>
  );
}

/** The price line under the panel: what is included, and what a fifth and beyond costs. */
export function connectionPrice(count: number): ReactNode {
  const extra = Math.max(0, count - CONN_FREE);
  const fee = `$${CONN_FEE.toFixed(2)}`;
  return extra ? (
    <>
      {count} connected. {CONN_FREE} are included and {extra} {extra === 1 ? "is" : "are"} beyond
      that, which adds <b>${(extra * CONN_FEE).toFixed(2)} a month</b> at {fee} each.
    </>
  ) : (
    <>
      {count} of your {CONN_FREE} included connections used. A fifth and beyond costs {fee} a month
      each.
    </>
  );
}

/**
 * "Institutions you have connected": empty, one button ("Connect your account through Flinks") with the
 * price before it; after the first, each institution with Remove, then "Do you have another account
 * to add? / Yes, add another account".
 */
export function ConnectPanel({
  via,
  sub,
  connected,
  onAdd,
  onRemove,
  available = false,
  atLimit = false,
  testId,
}: {
  via: Connector;
  sub: string;
  connected: string[];
  onAdd?: () => void;
  onRemove?: (i: number) => void;
  /** Connections are built (false: the button is disabled and says so). */
  available?: boolean;
  /** No more connections can be added (the button is disabled and says so). */
  atLimit?: boolean;
  testId?: string;
}) {
  return (
    <div className="card pad mt" data-testid={testId}>
      <h2 style={{ marginBottom: 4 }}>Institutions you have connected</h2>
      <p style={{ color: "var(--ink-3)", fontSize: 13.5, margin: "4px 0 14px" }}>{sub}</p>
      <div>
        {connected.map((name, i) => (
          <div className="connrow" key={name}>
            <span className="cktick" aria-hidden="true">
              <svg viewBox="0 0 12 12" fill="none">
                <path
                  d="M2.2 6.3 4.7 8.8 9.8 3.5"
                  stroke="#fff"
                  strokeWidth="1.9"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <b>{name}</b>
            <span className="mut">connected through {via}</span>
            <button type="button" className="rm" onClick={() => onRemove?.(i)}>
              Remove
            </button>
          </div>
        ))}
        {connected.length ? (
          <div className="connq">
            <span>Do you have another account to add?</span>
            <button type="button" className="ynb" onClick={onAdd} disabled={!available || atLimit}>
              Yes, add another account
            </button>
          </div>
        ) : (
          <div className="connfirst">
            <p className="hint" style={{ margin: "0 0 10px" }}>
              {connectionPrice(0)}
            </p>
            <button
              type="button"
              className="btn p"
              onClick={onAdd}
              disabled={!available || atLimit}
              data-testid={testId && `${testId}-first`}
            >
              Connect your account through {via}
            </button>
          </div>
        )}
        {connected.length > 0 && <p className="hint">{connectionPrice(connected.length)}</p>}
        {available && atLimit && (
          <p className="hint" data-testid={testId && `${testId}-limit`}>
            You have reached the connection limit.
          </p>
        )}
        {!available && (
          <p className="hint" data-testid={testId && `${testId}-soon`}>
            Connecting through {via} isn&rsquo;t available yet. Choose &ldquo;I&rsquo;ll type it in
            instead&rdquo; to carry on.
          </p>
        )}
      </div>
    </div>
  );
}
